// Quality↔Velocity (#2) — the "are we ramping without breaking things?" view.
// Velocity bars with a prod-escape-rate line overlaid (quality-adjusted velocity),
// plus Change Failure Rate and MTTR KPIs. Data: GET /api/pulse/quality-velocity.
//
// CFR here is a proxy: the share of releases that were emergency hotfixes — an
// honest signal already produced by the velocity pipeline, not a fragile
// deploy↔bug blame join. Labeled as such in the UI.

import { useCallback, useEffect, useMemo, useState } from 'react'
import {
    ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts'
import { PULSE_COLORS } from './pulseConfig.js'

const VELOCITY_COLOR = '#3b82f6'
const ESCAPE_COLOR = '#ef4444'

// betterWhen 'lower' → a negative delta is good (green).
function Kpi({ label, sublabel, value, unit, delta, betterWhen }) {
    const hasVal = value != null
    let deltaColor = PULSE_COLORS.textSubtle
    if (delta != null && delta !== 0) {
        const good = betterWhen === 'lower' ? delta < 0 : delta > 0
        deltaColor = good ? PULSE_COLORS.success : PULSE_COLORS.danger
    }
    const arrow = delta == null || delta === 0 ? '→' : delta < 0 ? '↓' : '↑'
    return (
        <div
            style={{
                background: PULSE_COLORS.bgInner,
                border: `1px solid ${PULSE_COLORS.border}`,
                borderRadius: '10px',
                padding: '16px',
                flex: '1 1 160px',
                minWidth: '160px',
            }}
        >
            <div style={{ color: PULSE_COLORS.text, fontWeight: 600, fontSize: '13px' }}>{label}</div>
            <div style={{ color: PULSE_COLORS.textMuted, fontSize: '11px', marginBottom: '10px' }}>{sublabel}</div>
            <div style={{ color: PULSE_COLORS.text, fontSize: '26px', fontWeight: 700 }}>
                {hasVal ? value : '—'}
                {hasVal && <span style={{ fontSize: '13px', fontWeight: 500 }}>{unit}</span>}
            </div>
            {delta != null && (
                <div style={{ color: deltaColor, fontSize: '12px', fontWeight: 600, marginTop: '4px' }}>
                    {arrow} {Math.abs(delta)}
                    {unit} vs prev sprint
                </div>
            )}
        </div>
    )
}

export default function QualityVelocity({ refreshSignal }) {
    const [data, setData] = useState(null)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)

    const load = useCallback(async () => {
        setLoading(true)
        setError(null)
        try {
            const r = await fetch('/api/pulse/quality-velocity?sprints=10')
            const j = await r.json()
            if (!r.ok) throw new Error(j?.error ?? `quality-velocity ${r.status}`)
            setData(j)
        } catch (e) {
            setError(e.message)
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        load()
    }, [load, refreshSignal])

    const chartData = useMemo(
        () =>
            (data?.series ?? []).map((s) => ({
                label: `S${s.sprintNumber}`,
                Velocity: s.velocity,
                'Escapes / 100pts': s.escapeRate,
            })),
        [data]
    )

    const latest = data?.latest
    const deltas = data?.deltas

    return (
        <div
            style={{
                background: PULSE_COLORS.cardBg,
                borderRadius: '12px',
                padding: '20px',
                border: `1px solid ${PULSE_COLORS.border}`,
            }}
        >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <div>
                    <h3 style={{ color: PULSE_COLORS.text, fontWeight: 600, fontSize: '16px', margin: 0 }}>
                        📉 Velocity vs Quality — is the ramp costing us?
                    </h3>
                    <p style={{ color: PULSE_COLORS.textMuted, fontSize: '13px', margin: '6px 0 0' }}>
                        If the escape-rate line climbs as velocity bars grow, throughput is coming at quality&apos;s
                        expense.
                    </p>
                </div>
                {loading && <span style={{ color: PULSE_COLORS.textMuted, fontSize: '12px' }}>Loading…</span>}
            </div>

            {error && <div style={{ color: PULSE_COLORS.danger, fontSize: '13px', marginBottom: '12px' }}>⚠ {error}</div>}

            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '20px' }}>
                <Kpi
                    label="Prod escape rate"
                    sublabel="escaped bugs / 100 pts"
                    value={latest?.escapeRate}
                    unit=""
                    delta={deltas?.escapeRate}
                    betterWhen="lower"
                />
                <Kpi
                    label="Change failure rate"
                    sublabel="hotfix share of releases (proxy)"
                    value={latest?.changeFailureRate != null ? Math.round(latest.changeFailureRate * 100) : null}
                    unit="%"
                    delta={deltas?.changeFailureRate != null ? Math.round(deltas.changeFailureRate * 100) : null}
                    betterWhen="lower"
                />
                <Kpi
                    label="MTTR (prod bugs)"
                    sublabel="mean created → resolved"
                    value={latest?.mttrDays}
                    unit="d"
                    delta={deltas?.mttrDays}
                    betterWhen="lower"
                />
                <Kpi
                    label="Prod escapes"
                    sublabel="this sprint"
                    value={latest?.prodEscapes}
                    unit=""
                    delta={null}
                    betterWhen="lower"
                />
            </div>

            {chartData.length > 0 && (
                <ResponsiveContainer width="100%" height={260}>
                    <ComposedChart data={chartData} margin={{ top: 8, right: 8, bottom: 8, left: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={PULSE_COLORS.border} />
                        <XAxis dataKey="label" tick={{ fill: PULSE_COLORS.textMuted, fontSize: 11 }} />
                        <YAxis yAxisId="left" tick={{ fill: PULSE_COLORS.textMuted, fontSize: 11 }} />
                        <YAxis
                            yAxisId="right"
                            orientation="right"
                            tick={{ fill: ESCAPE_COLOR, fontSize: 11 }}
                        />
                        <Tooltip
                            contentStyle={{
                                background: PULSE_COLORS.bgInner,
                                border: `1px solid ${PULSE_COLORS.border}`,
                                borderRadius: '8px',
                                color: PULSE_COLORS.text,
                            }}
                        />
                        <Legend wrapperStyle={{ fontSize: '12px' }} />
                        <Bar yAxisId="left" dataKey="Velocity" fill={VELOCITY_COLOR} radius={[4, 4, 0, 0]} />
                        <Line
                            yAxisId="right"
                            type="monotone"
                            dataKey="Escapes / 100pts"
                            stroke={ESCAPE_COLOR}
                            strokeWidth={2}
                            connectNulls
                        />
                    </ComposedChart>
                </ResponsiveContainer>
            )}
        </div>
    )
}
